import { useEffect, useState } from "react";
import { InfoHint } from "../components/InfoHint";
import { api, formatApiError, openSecureFile, markSectionSeen } from "../lib/api";
import { highlightMatch } from "../lib/highlightMatch";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Megaphone, Send, Paperclip, Trash2, Users, Building2, X, Info, AlertTriangle, AlertOctagon, FileText, Download, Mail, ChevronRight, ShieldCheck, ClipboardList, Printer, FileDown, PhoneCall, FilePlus, FolderOpen, Save } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { ModuliStudio } from "../components/ModuliStudio";
import { ModuliCondomino } from "../components/ModuliCondomino";
import { NumeriUtiliCondomino, NumeriUtiliStudio } from "../components/NumeriUtili";

const PRIO = {
  Info: { cls: "bg-sky-100 text-sky-800 border-sky-200", icon: Info },
  Importante: { cls: "bg-amber-100 text-amber-800 border-amber-200", icon: AlertTriangle },
  Urgente: { cls: "bg-red-100 text-red-800 border-red-200", icon: AlertOctagon },
};
const fmtDate = (s) => new Date(s).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
const emailSubject = (a) => `[Bacheca · ${a.priorita}] ${a.titolo}`;
// Iter122dd · firma dinamica dal Titolare del trattamento del tenant (fallback "Studio"),
// così ogni admin di tenant vede una preview email con la propria ragione sociale.
const emailBody = (a, tit) => {
  const firma = ((tit && tit.ragione_sociale) || "").trim() || "lo Studio";
  return `Avviso da ${firma}\n\n${a.titolo}\n\n${a.testo}\n\nAccedi all'app per consultare la Bacheca.`;
};

// Iter122fi · Genera una finestra di stampa (usata anche per "Esporta PDF" via dialog
// nativo del browser che salva come PDF). Include SOLO: data invio, titolo, firma dello Studio,
// corpo del testo, lista allegati. NON riporta destinatari/email/condominio/oggetto.
const stampaAvviso = (avv, tit, isPdf) => {
  const firma = ((tit && tit.ragione_sociale) || "").trim() || "Lo Studio";
  const data = new Date(avv.created_at).toLocaleString("it-IT", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
  const priorita = (avv.priorita || "").toString();
  const allegati = (avv.allegati || []).map((a) => a.original_filename || a.filename || "Allegato").filter(Boolean);
  const esc = (s) => String(s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  const html = `<!doctype html><html lang="it"><head><meta charset="utf-8"/><title>${esc(avv.titolo)}</title>
<style>
  @page { margin: 20mm; }
  body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; color: #1f2937; line-height: 1.55; }
  .meta { color: #6b7280; font-size: 12pt; margin-bottom: 4mm; }
  .badge { display: inline-block; padding: 2px 8px; border: 1px solid #d1d5db; border-radius: 999px; font-size: 10pt; font-weight: 700; margin-right: 6px; }
  h1 { font-size: 20pt; margin: 6mm 0 4mm; color: #111827; }
  .firma { font-weight: 700; margin-bottom: 4mm; }
  .body { white-space: pre-wrap; font-size: 12pt; }
  .allegati { margin-top: 8mm; border-top: 1px solid #e5e7eb; padding-top: 4mm; font-size: 10pt; color: #4b5563; }
  .allegati ul { margin: 2mm 0 0; padding-left: 5mm; }
</style></head><body>
  <div class="meta"><span class="badge">${esc(priorita)}</span>${esc(data)} · da ${esc(firma)}</div>
  <h1>${esc(avv.titolo)}</h1>
  <div class="firma">Avviso da ${esc(firma)}</div>
  <div class="body">${esc(avv.testo)}</div>
  ${allegati.length ? `<div class="allegati"><strong>Allegati:</strong><ul>${allegati.map((n) => `<li>${esc(n)}</li>`).join("")}</ul></div>` : ""}
  <script>window.onload=function(){setTimeout(function(){window.print();${isPdf ? "" : "setTimeout(function(){window.close()},500)"}},250)}<\/script>
</body></html>`;
  const w = window.open("", "_blank", "width=900,height=1100");
  if (!w) { toast.error("Consenti i popup per stampare o esportare in PDF"); return; }
  w.document.open(); w.document.write(html); w.document.close();
};

function PrioBadge({ p }) {
  const cfg = PRIO[p] || PRIO.Info; const I = cfg.icon;
  return <span className={`px-2.5 py-1 rounded-full text-xs font-bold border flex items-center gap-1 ${cfg.cls}`}><I className="w-3.5 h-3.5" /> {p}</span>;
}

function Allegati({ list }) {
  if (!list?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-3">
      {list.map((a) => (
        <button type="button" key={a.id} onClick={() => openSecureFile(`/files/${a.id}`)} data-testid={`allegato-${a.id}`}
          className="inline-flex items-center gap-2 text-sm bg-slate-100 hover:bg-slate-200 rounded-lg px-3 py-1.5 text-slate-700">
          {(a.tipo || "").includes("pdf") ? <FileText className="w-4 h-4" /> : <Paperclip className="w-4 h-4" />}
          <span className="max-w-[180px] truncate">{a.nome || "Allegato"}</span>
          <Download className="w-3.5 h-3.5" />
        </button>
      ))}
    </div>
  );
}

export default function Bacheca() {
  const { user } = useAuth();
  const isAdmin = user.role === "admin";
  // Iter122ar · tab switcher "Avvisi" (default) · "Moduli"
  const [tab, setTab] = useState("avvisi");
  // Iter122as · badge condòmino: numero moduli non ancora compilati
  const [nModuliDaCompilare, setNModuliDaCompilare] = useState(0);
  // Iter123k190 · badge admin: numero di nuove compilazioni moduli arrivate dai condòmini
  const [nCompilazioniNuove, setNCompilazioniNuove] = useState(0);
  // Iter122cz · snapshot del "last_seen.bacheca" precedente all'ingresso: usato per
  // distinguere gli avvisi già letti (compatti) da quelli nuovi (card estesa).
  const [prevSeen, setPrevSeen] = useState(null); // null = ancora sconosciuto
  useEffect(() => {
    if (isAdmin) {
      // Iter123k190 · lato Studio: carico il contatore sidebar per settare il badge sulla tab Moduli
      api.get("/me/messaggi-non-letti")
        .then((r) => setNCompilazioniNuove(r.data?.moduli_compilati || 0))
        .catch(() => {});
      return;
    }
    // Iter122at · azzera il badge sidebar "bacheca" per il condòmino appena entra
    // Iter122cz · cattura il timestamp PRECEDENTE per pilotare la vista "letti/non letti"
    markSectionSeen("bacheca").then((res) => setPrevSeen((res && res.previous) || ""));
    // Iter122k2 · Badge Moduli: conta SOLO i moduli aggiunti dopo l'ultima visita alla tab Moduli.
    // Leggo `last_seen.moduli` dall'oggetto user (già caricato da /auth/me): non aggiorno
    // il timestamp finché il condòmino non clicca effettivamente la tab.
    const lastSeenModuli = (user && user.last_seen && user.last_seen.moduli) || "";
    api.get("/condomino/moduli").then((r) => {
      const moduli = r.data.moduli || [];
      // Al primo accesso (`last_seen.moduli` vuoto) il badge NON compare (tutti considerati "già visti").
      const nuovi = lastSeenModuli
        ? moduli.filter((m) => (m.created_at || "") > lastSeenModuli).length
        : 0;
      setNModuliDaCompilare(nuovi);
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);
  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-brand flex items-center justify-center"><Megaphone className="w-7 h-7 text-white" /></div>
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Bacheca</h1>
            <InfoHint section="bacheca" />
          </div>
          <p className="text-base text-slate-500">{isAdmin ? "Invia avvisi ai condòmini e consulta lo storico" : "Gli avvisi dello Studio per te"}</p>
        </div>
      </div>
      {/* Iter122ar · tab in alto (Avvisi / Moduli) */}
      <div className="flex flex-wrap items-center gap-1 border-b-2 border-slate-200 mb-4" data-testid="bacheca-tabs">
        <button
          data-testid="bacheca-tab-avvisi"
          onClick={() => setTab("avvisi")}
          className={`flex items-center gap-2 px-4 py-2.5 font-bold text-sm border-b-[3px] -mb-0.5 rounded-t-md transition-colors ${tab === "avvisi" ? "bg-[#FBEEF3] border-brand text-brand" : "border-transparent text-slate-500 hover:text-brand hover:bg-[#FBEEF3]/40"}`}
        >
          <Megaphone className="w-4 h-4" /> Avvisi
        </button>
        <button
          data-testid="bacheca-tab-moduli"
          onClick={() => {
            setTab("moduli");
            // Iter122k2 · Marca "moduli" come visto solo quando l'utente apre la tab
            if (!isAdmin && nModuliDaCompilare > 0) {
              markSectionSeen("moduli");
              setNModuliDaCompilare(0);
            }
            // Iter123k190 · Admin: marca `moduli_admin` → azzera badge sidebar + tab
            if (isAdmin && nCompilazioniNuove > 0) {
              markSectionSeen("moduli_admin");
              setNCompilazioniNuove(0);
            }
          }}
          className={`relative flex items-center gap-2 px-4 py-2.5 font-bold text-sm border-b-[3px] -mb-0.5 rounded-t-md transition-colors ${tab === "moduli" ? "bg-[#FBEEF3] border-brand text-brand" : "border-transparent text-slate-500 hover:text-brand hover:bg-[#FBEEF3]/40"}`}
        >
          <ClipboardList className="w-4 h-4" /> Moduli
          {!isAdmin && nModuliDaCompilare > 0 && (
            <span data-testid="bacheca-moduli-badge"
              className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-extrabold flex items-center justify-center">
              {nModuliDaCompilare}
            </span>
          )}
          {/* Iter123k190 · Badge admin: nuove compilazioni ricevute */}
          {isAdmin && nCompilazioniNuove > 0 && (
            <span data-testid="bacheca-moduli-admin-badge"
              className="ml-1 min-w-[20px] h-5 px-1.5 rounded-full bg-red-600 text-white text-[10px] font-extrabold flex items-center justify-center">
              {nCompilazioniNuove}
            </span>
          )}
        </button>
        {/* Iter122ju · Tab "Numeri utili" */}
        <button
          data-testid="bacheca-tab-numeri-utili"
          onClick={() => setTab("numeri_utili")}
          className={`flex items-center gap-2 px-4 py-2.5 font-bold text-sm border-b-[3px] -mb-0.5 rounded-t-md transition-colors ${tab === "numeri_utili" ? "bg-[#FBEEF3] border-brand text-brand" : "border-transparent text-slate-500 hover:text-brand hover:bg-[#FBEEF3]/40"}`}
        >
          <PhoneCall className="w-4 h-4" /> Numeri utili
        </button>
      </div>
      {tab === "avvisi" && (isAdmin ? <AdminBacheca /> : <CondominoBacheca prevSeen={prevSeen} />)}
      {tab === "moduli" && (isAdmin ? <ModuliStudio /> : <ModuliCondomino onCompilato={() => setNModuliDaCompilare(0)} />)}
      {tab === "numeri_utili" && (isAdmin ? <NumeriUtiliStudio /> : <NumeriUtiliCondomino />)}
    </div>
  );
}

function CondominoBacheca({ prevSeen }) {
  const [list, setList] = useState([]);
  const [detail, setDetail] = useState(null);
  useEffect(() => { api.get("/avvisi/miei").then((r) => setList(r.data)).catch(() => {}); }, []);
  if (list.length === 0) return <Card className="p-5 text-center text-slate-500" data-testid="bacheca-empty">Nessun avviso al momento.</Card>;
  // Iter122cz · un avviso è "letto" se creato prima dell'ultima visita del condòmino.
  // Se prevSeen è null (ancora in caricamento) mostriamo tutto come non-letto: al peggio
  // vediamo card estese per un tick, mai il contrario.
  const isRead = (a) => !!prevSeen && !!a.created_at && a.created_at <= prevSeen;
  const nonLetti = list.filter((a) => !isRead(a));
  const letti = list.filter((a) => isRead(a));
  return (
    <>
      <div className="space-y-4" data-testid="bacheca-condomino-list">
        {nonLetti.map((a) => (
          <Card key={a.id} className="p-5 border-2 border-brand/40 bg-[#FBEEF3]/30" data-testid={`avviso-${a.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-brand shrink-0" title="Nuovo" data-testid={`avviso-nuovo-dot-${a.id}`} />
                <h3 className="font-bold text-xl text-slate-800 min-w-0 truncate">{a.titolo}</h3>
              </div>
              <PrioBadge p={a.priorita} />
            </div>
            <div className="text-xs text-slate-400 mb-3">{fmtDate(a.created_at)}</div>
            <p className="text-base text-slate-700 whitespace-pre-wrap">{a.testo}</p>
            <Allegati list={a.allegati} />
          </Card>
        ))}
        {letti.length > 0 && (
          <div data-testid="bacheca-letti-section">
            {nonLetti.length > 0 && (
              <div className="flex items-center gap-2 pt-3 pb-1 text-xs font-bold uppercase tracking-wider text-slate-400">
                <span className="h-px flex-1 bg-slate-200" />
                <span>Già letti ({letti.length})</span>
                <span className="h-px flex-1 bg-slate-200" />
              </div>
            )}
            <div className="rounded-xl border-2 border-slate-100 overflow-hidden bg-white">
              {letti.map((a, i) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setDetail(a)}
                  data-testid={`avviso-letto-${a.id}`}
                  className={`w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#FBEEF3]/50 transition-colors ${i > 0 ? "border-t border-slate-100" : ""}`}
                >
                  <PrioBadge p={a.priorita} />
                  <span className="font-semibold text-slate-800 flex-1 min-w-0 truncate">{a.titolo}</span>
                  {a.allegati?.length > 0 && (
                    <span className="hidden sm:inline-flex items-center gap-1 text-xs text-slate-400"><Paperclip className="w-3 h-3" /> {a.allegati.length}</span>
                  )}
                  <span className="hidden sm:inline text-xs text-slate-400 shrink-0">{fmtDate(a.created_at)}</span>
                  <ChevronRight className="w-4 h-4 text-slate-400 shrink-0" />
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto" data-testid="avviso-condomino-detail">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-2xl font-display pr-6">
                  <Megaphone className="w-6 h-6 text-brand shrink-0" /> {detail.titolo}
                </DialogTitle>
                <DialogDescription className="sr-only">Dettaglio avviso</DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <PrioBadge p={detail.priorita} />
                <span className="text-xs text-slate-400">{fmtDate(detail.created_at)}</span>
              </div>
              <p className="text-base text-slate-700 whitespace-pre-wrap">{detail.testo}</p>
              <Allegati list={detail.allegati} />
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function AdminBacheca() {
  const { user } = useAuth();
  const titolare = user?.titolare_trattamento;
  const empty = { titolo: "", testo: "", priorita: "Info", scope: "tutti" };
  const [form, setForm] = useState(empty);
  const [condomini, setCondomini] = useState([]);
  const [residenti, setResidenti] = useState([]);
  const [selCond, setSelCond] = useState(new Set());
  const [selRes, setSelRes] = useState(new Set());
  const [allegati, setAllegati] = useState([]);
  const [search, setSearch] = useState("");
  const [sending, setSending] = useState(false);
  const [storico, setStorico] = useState([]);
  const [detail, setDetail] = useState(null);
  const [resending, setResending] = useState(false);
  const [filtroStorico, setFiltroStorico] = useState("");
  const [soloConsiglieri, setSoloConsiglieri] = useState(false);
  // Iter123k248 · lista arricchita consiglieri + filtri (condominio, scala) per lo scope "consiglieri"
  const [consiglieri, setConsiglieri] = useState([]);
  const [cFiltroCond, setCFiltroCond] = useState("");
  const [cFiltroScala, setCFiltroScala] = useState("");
  const [cSearch, setCSearch] = useState("");
  const [selCons, setSelCons] = useState(new Set());  // residente_ids selezionati
  // All'ingresso in scope "consiglieri" selezioniamo tutti i consiglieri visibili.
  const [cInitAll, setCInitAll] = useState(false);
  // Iter123k249 · bozze avviso
  const [bozze, setBozze] = useState([]);
  const [bozzaId, setBozzaId] = useState(null);  // null = sta componendo da zero; altrimenti sta modificando quella bozza
  const [savingDraft, setSavingDraft] = useState(false);
  const [showBozze, setShowBozze] = useState(false);

  const selCondNome = condomini.find((c) => c.id === filtroStorico)?.nome;
  const storicoFiltrato = filtroStorico
    ? storico.filter((a) => a.scope === "tutti" || (a.condomini_nomi || []).includes(selCondNome))
    : storico;

  const load = () => api.get("/avvisi").then((r) => setStorico(r.data)).catch(() => {});
  useEffect(() => {
    api.get("/condomini").then((r) => setCondomini(r.data)).catch(() => {});
    api.get("/residenti").then((r) => setResidenti(r.data.filter((x) => x.has_account))).catch(() => {});
    // Iter123k248 · lista consiglieri arricchita (nome · condominio · codice unità · scala)
    api.get("/avvisi/consiglieri").then((r) => {
      setConsiglieri(r.data || []);
    }).catch(() => {});
    // Iter123k249 · elenco bozze del tenant
    api.get("/avvisi/bozze").then((r) => setBozze(r.data || [])).catch(() => {});
    load();
    const raw = localStorage.getItem("ginius_prefill_avviso");
    if (raw) { try { const p = JSON.parse(raw); setForm((f) => ({ ...f, titolo: p.titolo || "", testo: p.testo || "" })); if (Array.isArray(p.allegati) && p.allegati.length) setAllegati(p.allegati); toast.info(p.allegati?.length ? "Modello caricato con allegato predefinito: rivedi e completa l'invio." : "Modello caricato: rivedi e completa l'invio."); } catch { /* ignore */ } localStorage.removeItem("ginius_prefill_avviso"); }
  }, []);

  // Scale disponibili per il condominio filtrato (o tutte se nessun filtro condominio)
  const consFiltered = consiglieri.filter((c) => {
    if (cFiltroCond && c.condominio_id !== cFiltroCond) return false;
    if (cFiltroScala && (c.scala || "").toLowerCase() !== cFiltroScala.toLowerCase()) return false;
    if (cSearch) {
      const q = cSearch.toLowerCase();
      const hay = `${c.nome} ${c.condominio_nome} ${c.codice_unita} ${c.scala} ${c.palazzina}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });
  // Iter123k249 · conteggio reale destinatari effettivi (visibili ∩ selezionati)
  const consEffectiveCount = consFiltered.filter((c) => selCons.has(c.id)).length;
  const invioDisabled =
    sending
    || (form.scope === "consiglieri" && consEffectiveCount === 0);
  // Scale disponibili per il condominio filtrato (o tutte se nessun filtro condominio)
  const scaleDisponibili = Array.from(new Set(
    consiglieri
      .filter((c) => !cFiltroCond || c.condominio_id === cFiltroCond)
      .map((c) => (c.scala || "").trim())
      .filter(Boolean)
  )).sort();

  // Pre-seleziona tutti i consiglieri visibili al primo ingresso in scope="consiglieri"
  useEffect(() => {
    if (form.scope === "consiglieri" && !cInitAll && consiglieri.length > 0) {
      setSelCons(new Set(consiglieri.map((c) => c.id)));
      setCInitAll(true);
    }
    if (form.scope !== "consiglieri" && cInitAll) {
      // reset flag così rientrando lo riproponiamo tutti selezionati
      setCInitAll(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.scope, consiglieri]);

  const toggle = (set, fn, id) => { const n = new Set(set); n.has(id) ? n.delete(id) : n.add(id); fn(n); };

  const upload = async (e) => {
    const files = Array.from(e.target.files || []); if (files.length === 0) return;
    try {
      for (const f of files) {
        const fd = new FormData(); fd.append("file", f);
        const { data } = await api.post("/avvisi/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setAllegati((a) => [...a, data]);
      }
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Caricamento non riuscito"); }
    e.target.value = "";
  };

  const invia = async () => {
    if (!form.titolo.trim() || !form.testo.trim()) { toast.error("Inserisci titolo e testo"); return; }
    if (form.scope === "condomini" && selCond.size === 0) { toast.error("Seleziona almeno un condominio"); return; }
    if (form.scope === "singoli" && selRes.size === 0) { toast.error("Seleziona almeno un condòmino"); return; }
    if (form.scope === "consiglieri") {
      if (selCons.size === 0) { toast.error("Seleziona almeno un consigliere"); return; }
      // Iter123k249 fix · verifica l'intersezione effettiva post-filtro: se è vuota,
      // l'invio sarebbe interpretato dal backend come "nessun narrow-down" e andrebbe
      // a tutti i consiglieri — comportamento NON voluto dall'utente.
      const visibleIds = new Set(consFiltered.map((c) => c.id));
      const effective = Array.from(selCons).filter((id) => visibleIds.has(id));
      if (effective.length === 0) {
        toast.error("Nessun destinatario effettivo: allarga i filtri o seleziona almeno un consigliere visibile");
        return;
      }
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        condominio_ids: Array.from(selCond),
        residente_ids: Array.from(selRes),
        allegati,
      };
      // Iter123k248 · lo scope "consiglieri" invia come residente_ids la selezione
      // precisa (post-filtro condominio/scala). Il backend intersecherà con il filtro
      // `consigliere=True` così, se la selezione cambia nel frattempo (nuovo
      // consigliere aggiunto in anagrafica), non si inviano avvisi fuori perimetro.
      if (form.scope === "consiglieri") {
        // Iter123k249 fix · invia SOLO i consiglieri visibili (post-filtro) ancora
        // selezionati. Senza intersezione, cambiare il filtro per scala/condominio
        // non avrebbe effetto sull'invio.
        const visibleIds = new Set(consFiltered.map((c) => c.id));
        payload.residente_ids = Array.from(selCons).filter((id) => visibleIds.has(id));
        payload.condominio_ids = cFiltroCond ? [cFiltroCond] : [];
      }
      const { data } = await api.post("/avvisi", payload);
      toast.success(`Avviso inviato a ${data.destinatari_count} condòmini (${data.email_inviati} email)`);
      // Iter123k249 · se stavo "promuovendo" una bozza, cancellala in automatico.
      if (bozzaId) {
        try { await api.delete(`/avvisi/bozze/${bozzaId}`); } catch { /* ignore */ }
        setBozzaId(null);
        loadBozze();
      }
      setForm(empty); setSelCond(new Set()); setSelRes(new Set()); setSelCons(new Set()); setCInitAll(false); setAllegati([]);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Invio non riuscito"); }
    setSending(false);
  };

  const elimina = async (a) => {
    if (!window.confirm("Eliminare questo avviso dallo storico?")) return;
    try { await api.delete(`/avvisi/${a.id}`); load(); } catch { toast.error("Eliminazione non riuscita"); }
  };

  // Iter123k249 · bozze avvisi (salva/carica/aggiorna/elimina)
  const loadBozze = () => api.get("/avvisi/bozze").then((r) => setBozze(r.data || [])).catch(() => {});
  // Snapshot del form attuale pronto da inviare al backend bozza.
  const draftPayload = () => {
    const payload = {
      titolo: form.titolo, testo: form.testo, priorita: form.priorita, scope: form.scope,
      condominio_ids: Array.from(selCond), residente_ids: Array.from(selRes),
      allegati,
    };
    if (form.scope === "consiglieri") {
      // Iter123k249 fix · stesso contratto di invia(): la bozza memorizza solo i
      // consiglieri visibili e selezionati (coerente con l'anteprima mostrata).
      const visibleIds = new Set(consFiltered.map((c) => c.id));
      payload.residente_ids = Array.from(selCons).filter((id) => visibleIds.has(id));
      payload.condominio_ids = cFiltroCond ? [cFiltroCond] : [];
    }
    return payload;
  };
  const salvaBozza = async () => {
    if (!form.titolo.trim() && !form.testo.trim() && allegati.length === 0) {
      toast.error("Scrivi almeno un titolo, un testo o un allegato prima di salvare la bozza");
      return;
    }
    setSavingDraft(true);
    try {
      const payload = draftPayload();
      if (bozzaId) {
        await api.put(`/avvisi/bozze/${bozzaId}`, payload);
        toast.success("Bozza aggiornata");
      } else {
        const { data } = await api.post("/avvisi/bozze", payload);
        setBozzaId(data.id);
        toast.success("Bozza salvata · potrai riprenderla da «Bozze»");
      }
      loadBozze();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Salvataggio bozza non riuscito"); }
    setSavingDraft(false);
  };
  const nuovaBozza = () => {
    setForm(empty); setSelCond(new Set()); setSelRes(new Set()); setSelCons(new Set());
    setCInitAll(false); setAllegati([]); setBozzaId(null);
    setCFiltroCond(""); setCFiltroScala(""); setCSearch("");
    toast.info("Compila una nuova bozza");
  };
  const caricaBozza = (b) => {
    setForm({ titolo: b.titolo || "", testo: b.testo || "", priorita: b.priorita || "Info", scope: b.scope || "tutti" });
    setSelCond(new Set(b.condominio_ids || []));
    setSelRes(new Set(b.residente_ids || []));
    setSelCons(new Set(b.residente_ids || []));
    setCInitAll(true);  // non ri-sovrascriviamo con "tutti"
    setCFiltroCond(((b.condominio_ids || []).length === 1) ? b.condominio_ids[0] : "");
    setCFiltroScala(""); setCSearch("");
    setAllegati(b.allegati || []);
    setBozzaId(b.id);
    setShowBozze(false);
    toast.success(`Bozza «${b.titolo || "(senza titolo)"}» caricata`);
  };
  const eliminaBozza = async (b, ev) => {
    if (ev) ev.stopPropagation();
    if (!window.confirm(`Eliminare la bozza «${b.titolo || "(senza titolo)"}»?`)) return;
    try {
      await api.delete(`/avvisi/bozze/${b.id}`);
      if (bozzaId === b.id) setBozzaId(null);
      loadBozze();
    } catch { toast.error("Eliminazione bozza non riuscita"); }
  };

  // Iter122dk · firma testuale dal tenant (branding-assets) — usata nel form come blocco appendibile.
  const firmaTesto = ((titolare && titolare.ragione_sociale) || "").trim();
  const appendFirma = () => {
    if (!firmaTesto) { toast.info("Compila prima la ragione sociale nel Profilo Studio (Impostazioni)"); return; }
    const block = `\n\nCordiali saluti,\n${firmaTesto}`;
    setForm((f) => f.testo.endsWith(block) ? f : { ...f, testo: (f.testo || "").replace(/\s+$/,"") + block });
    toast.success("Firma inserita in fondo al testo");
  };

  const reinvia = async (a) => {
    if (!window.confirm("Reinviare questo avviso agli stessi destinatari via email?")) return;
    setResending(true);
    try {
      const { data } = await api.post(`/avvisi/${a.id}/reinvia`);
      toast.success(data.inviati > 0 ? `Avviso reinviato a ${data.inviati} destinatari` : "Nessun destinatario con email valida da reinviare");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Reinvio non riuscito"); }
    setResending(false);
  };

  const resFiltered = residenti
    .filter((r) => !soloConsiglieri || r.consigliere)
    .filter((r) => `${r.nome} ${r.condominio_nome}`.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      {/* COMPOSE */}
      <Card className="p-4 border-2 h-fit min-w-0" data-testid="bacheca-compose">
        <h2 className="font-display text-xl font-bold mb-4">Nuovo avviso</h2>
        <label className="text-sm font-semibold text-slate-600">Titolo<span className="text-red-500 ml-0.5">*</span></label>
        <Input data-testid="avviso-titolo" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} placeholder="Es. Interruzione acqua martedì" className="h-12 mt-1 mb-3" />
        <label className="text-sm font-semibold text-slate-600">Testo<span className="text-red-500 ml-0.5">*</span></label>
        <Textarea data-testid="avviso-testo" value={form.testo} onChange={(e) => setForm({ ...form, testo: e.target.value })} placeholder="Scrivi il messaggio per i condòmini..." className="min-h-[120px] mt-1 mb-2" />
        <div className="flex items-center gap-2 mb-3">
          <button type="button" data-testid="avviso-inserisci-firma" onClick={appendFirma}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold border border-slate-300 bg-white text-slate-700 hover:bg-slate-50">
            <FileText className="w-3.5 h-3.5" /> Inserisci firma dello Studio
          </button>
          {firmaTesto ? (
            <span className="text-xs text-slate-500">firma: <span className="font-mono">{firmaTesto}</span></span>
          ) : (
            <span className="text-xs text-amber-600">firma non configurata — completa il Profilo Studio</span>
          )}
        </div>

        <label className="text-sm font-semibold text-slate-600">Priorità</label>
        <div className="flex gap-2 mt-1 mb-4">
          {Object.keys(PRIO).map((p) => (
            <button key={p} type="button" data-testid={`prio-${p}`} onClick={() => setForm({ ...form, priorita: p })}
              className={`px-3 py-2 rounded-lg text-sm font-semibold border-2 transition-colors ${form.priorita === p ? "border-brand bg-[#FBEEF3] text-brand" : "border-slate-200 text-slate-500"}`}>{p}</button>
          ))}
        </div>

        <label className="text-sm font-semibold text-slate-600">Destinatari</label>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-1 mb-3">
          {[["tutti", "Tutti", Users], ["consiglieri", "Consiglieri", ShieldCheck], ["condomini", "Condomìni", Building2], ["singoli", "Singoli", Users]].map(([v, l, I]) => (
            <button key={v} type="button" data-testid={`scope-${v}`} onClick={() => setForm({ ...form, scope: v })}
              className={`px-2 py-2.5 rounded-lg text-sm font-semibold border-2 flex items-center justify-center gap-1 ${form.scope === v ? "border-brand bg-[#FBEEF3] text-brand" : "border-slate-200 text-slate-500"}`}>
              <I className="w-4 h-4" /> {l}
            </button>
          ))}
        </div>

        {form.scope === "consiglieri" && (
          <div className="mb-3" data-testid="scope-consiglieri-blocco">
            <div className="mb-2 p-3 rounded-lg border-2 border-[#EBC5D2] bg-[#FBEEF3]/50 text-sm text-slate-700" data-testid="scope-consiglieri-info">
              L&apos;avviso sarà inviato ai condòmini contrassegnati come <b>Consigliere</b> (con account attivo),
              <b> solo tra quelli visibili e selezionati qui sotto</b>. Usa i filtri per limitarlo ad un singolo
              condominio o ad una specifica scala/edificio: i consiglieri nascosti dai filtri NON riceveranno l&apos;avviso.
              {consiglieri.length === 0
                && <> <br />⚠️ Nessun consigliere ancora contrassegnato: assegna il ruolo dall&apos;Anagrafica Condòmini.</>}
            </div>
            {consiglieri.length > 0 && (
              <>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <select data-testid="cons-filtro-condominio"
                    value={cFiltroCond}
                    onChange={(e) => { setCFiltroCond(e.target.value); setCFiltroScala(""); }}
                    className="h-10 px-2 border-2 border-slate-200 rounded-lg text-sm bg-white min-w-[180px]">
                    <option value="">Tutti i condomìni</option>
                    {Array.from(new Set(consiglieri.map((c) => c.condominio_id)))
                      .map((cid) => {
                        const nome = consiglieri.find((x) => x.condominio_id === cid)?.condominio_nome || "—";
                        return <option key={cid} value={cid}>{nome}</option>;
                      })}
                  </select>
                  <select data-testid="cons-filtro-scala"
                    value={cFiltroScala}
                    onChange={(e) => setCFiltroScala(e.target.value)}
                    disabled={scaleDisponibili.length === 0}
                    className="h-10 px-2 border-2 border-slate-200 rounded-lg text-sm bg-white min-w-[120px] disabled:bg-slate-50 disabled:text-slate-400">
                    <option value="">Tutte le scale</option>
                    {scaleDisponibili.map((s) => (
                      <option key={s} value={s}>Scala {s}</option>
                    ))}
                  </select>
                  <Input value={cSearch} onChange={(e) => setCSearch(e.target.value)}
                    placeholder="Cerca nome o codice unità..."
                    data-testid="cons-search"
                    className="h-10 flex-1 min-w-[140px]" />
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-2 text-xs text-slate-600">
                  <Button type="button" size="sm" variant="outline" className="h-8"
                    data-testid="cons-sel-tutti"
                    onClick={() => setSelCons(new Set([...selCons, ...consFiltered.map((c) => c.id)]))}>
                    Seleziona tutti i visibili
                  </Button>
                  <Button type="button" size="sm" variant="outline" className="h-8"
                    data-testid="cons-sel-nessuno"
                    onClick={() => {
                      const n = new Set(selCons);
                      consFiltered.forEach((c) => n.delete(c.id));
                      setSelCons(n);
                    }}>
                    Deseleziona i visibili
                  </Button>
                  <span className="basis-full sm:basis-auto sm:ml-auto" data-testid="cons-counter">
                    {/* Iter123k249 · conteggio reale destinatari: solo visibili ∩ selezionati */}
                    <b>{consFiltered.filter((c) => selCons.has(c.id)).length}</b> destinatari effettivi
                    {" · "}
                    <b>{consFiltered.length}</b> visibili
                  </span>
                </div>
                <div className="border-2 rounded-lg p-2 max-h-56 overflow-auto space-y-0.5"
                  data-testid="scope-consiglieri-list">
                  {consFiltered.map((c) => (
                    <label key={c.id}
                      className="flex items-center gap-2 py-1 px-2 rounded cursor-pointer hover:bg-[#FBEEF3]/50"
                      data-testid={`cons-row-${c.id}`}>
                      <input type="checkbox" className="w-4 h-4 accent-brand"
                        checked={selCons.has(c.id)}
                        onChange={() => toggle(selCons, setSelCons, c.id)} />
                      <ShieldCheck className="w-3.5 h-3.5 text-brand shrink-0" />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-semibold text-slate-800 truncate">
                          {highlightMatch(c.nome, cSearch)}
                        </div>
                        <div className="text-[11px] text-slate-500 truncate">
                          {highlightMatch(c.condominio_nome, cSearch)}
                          {c.palazzina ? <> · Edificio <b>{c.palazzina}</b></> : null}
                          {c.scala ? <> · Scala <b>{c.scala}</b></> : null}
                          {c.codice_unita ? <> · Unità <b>{highlightMatch(c.codice_unita, cSearch)}</b></> : null}
                        </div>
                      </div>
                    </label>
                  ))}
                  {consFiltered.length === 0 && (
                    <div className="text-sm text-slate-400 p-3 text-center">
                      Nessun consigliere corrisponde ai filtri.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {form.scope === "condomini" && (
          <div className="border-2 rounded-lg p-3 max-h-56 overflow-auto mb-3 space-y-1" data-testid="scope-condomini-list">
            {/* Iter122jo/jq · Legenda: canali di consegna */}
            <div className="text-[11px] text-slate-500 mb-2 space-y-0.5">
              <div className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                <b>vede in-app</b> · account con login attivo (invito accettato)
              </div>
              <div className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-sky-500" />
                <b>via mail</b> · anagrafica con email ma senza login
              </div>
              <div className="flex items-center gap-1">
                <span className="inline-block w-2 h-2 rounded-full bg-slate-300" />
                <b>non pronto</b> · migrazione o anagrafica incompleta
              </div>
            </div>
            {condomini.map((c) => {
              const inApp = c.account_attivi_count || 0;
              const soloMail = c.account_email_only_count || 0;
              const isReady = !!c.bacheca_ready;
              return (
                <label key={c.id}
                  className={`flex items-center gap-2 py-1 px-2 rounded-md cursor-pointer ${
                    isReady ? "bg-emerald-50 hover:bg-emerald-100" : "hover:bg-slate-50"
                  }`}
                  data-testid={`bacheca-cond-${c.id}${isReady ? "-ready" : ""}`}
                  title={isReady
                    ? `${inApp} vedono in-app · ${soloMail} ricevono solo via mail`
                    : (!c.is_migrato ? "Condominio non ancora migrato al nuovo modello unità" : "Nessun account attivo o email in anagrafica")}
                >
                  <input type="checkbox" className="w-4 h-4 accent-brand" checked={selCond.has(c.id)} onChange={() => toggle(selCond, setSelCond, c.id)} />
                  <span className={`text-base flex-1 min-w-0 ${isReady ? "text-emerald-800 font-semibold" : "text-slate-700"}`}>
                    {c.nome}
                  </span>
                  {inApp > 0 && (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-emerald-700 bg-white border border-emerald-300 rounded-full px-1.5 py-0.5" title="Vedono in-app">
                      {inApp} in-app
                    </span>
                  )}
                  {soloMail > 0 && (
                    <span className="text-[10px] font-bold uppercase tracking-wide text-sky-700 bg-white border border-sky-300 rounded-full px-1.5 py-0.5" title="Ricevono solo la mail">
                      {soloMail} mail
                    </span>
                  )}
                </label>
              );
            })}
          </div>
        )}
        {form.scope === "singoli" && (
          <div className="mb-3">
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cerca condòmino..." className="h-10 flex-1 min-w-[140px]" />
              <label className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-700 cursor-pointer px-2 h-10 border-2 border-slate-200 rounded-lg hover:border-[#C98AA0]" data-testid="scope-filtro-consiglieri">
                <input type="checkbox" className="w-4 h-4 accent-brand" checked={soloConsiglieri}
                  onChange={(e) => setSoloConsiglieri(e.target.checked)} />
                <ShieldCheck className="w-4 h-4 text-brand" /> Solo consiglieri
              </label>
              {soloConsiglieri && resFiltered.length > 0 && (
                <Button type="button" size="sm" variant="outline" className="h-10" data-testid="scope-seleziona-tutti-consiglieri"
                  onClick={() => setSelRes(new Set([...selRes, ...resFiltered.map((r) => r.id)]))}>Seleziona tutti</Button>
              )}
            </div>
            <div className="border-2 rounded-lg p-3 max-h-48 overflow-auto space-y-1" data-testid="scope-singoli-list">
              {resFiltered.map((r) => (
                <label key={r.id} className="flex items-center gap-2 py-1 cursor-pointer">
                  <input type="checkbox" className="w-4 h-4 accent-brand" checked={selRes.has(r.id)} onChange={() => toggle(selRes, setSelRes, r.id)} />
                  <span className="text-base flex-1 min-w-0 truncate">
                    {highlightMatch(r.nome, search)}
                    {r.consigliere && <span className="ml-1.5 inline-flex items-center gap-0.5 px-1 py-0.5 rounded text-[10px] font-bold bg-[#F0D9E1] text-brand align-middle"><ShieldCheck className="w-2.5 h-2.5" /></span>}
                    <span className="text-slate-400 text-sm"> · {highlightMatch(r.condominio_nome, search)}</span>
                  </span>
                </label>
              ))}
              {resFiltered.length === 0 && <div className="text-sm text-slate-400">{soloConsiglieri ? "Nessun consigliere che corrisponda alla ricerca." : "Nessun condòmino con account."}</div>}
            </div>
          </div>
        )}

        {allegati.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3">
            {allegati.map((a, i) => (
              <span key={a.id} className="inline-flex items-center gap-2 text-sm bg-slate-100 rounded-lg px-3 py-1.5">
                <Paperclip className="w-4 h-4" /> <span className="max-w-[140px] truncate">{a.nome}</span>
                <button onClick={() => setAllegati(allegati.filter((_, j) => j !== i))}><X className="w-4 h-4 text-slate-500" /></button>
              </span>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-3 mt-2">
          <label data-testid="avviso-allega" className="inline-flex items-center gap-2 h-12 px-4 rounded-md border-2 cursor-pointer text-slate-600 hover:bg-slate-50">
            <Paperclip className="w-5 h-5" /> Allega (PDF/immagine)
            <input type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={upload} />
          </label>
          {/* Iter123k249 · salva come bozza o aggiorna la bozza aperta */}
          <Button type="button" variant="outline" data-testid="avviso-salva-bozza"
            onClick={salvaBozza} disabled={savingDraft}
            className="h-12 px-4 border-2">
            <Save className="w-5 h-5 mr-2" />
            {savingDraft
              ? "Salvo..."
              : (bozzaId ? "Aggiorna bozza" : "Salva come bozza")}
          </Button>
          <Button data-testid="avviso-invia" disabled={invioDisabled} onClick={invia} className="h-12 flex-1 min-w-[160px] bg-brand hover:bg-brand-dark font-bold disabled:opacity-50">
            <Send className="w-5 h-5 mr-2" /> {sending ? "Invio..." : (bozzaId ? "Invia e cancella bozza" : "Invia avviso")}
          </Button>
        </div>
      </Card>

      {/* STORICO */}
      <div data-testid="bacheca-storico" className="min-w-0">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="font-display text-xl font-bold">Storico avvisi ({storicoFiltrato.length})</h2>
          {/* Iter123k249 · trigger compatto Bozze: sta fra lo storico e il filtro condomini */}
          <Button type="button" variant="outline" size="sm"
            onClick={() => setShowBozze((v) => !v)}
            data-testid="bacheca-bozze-toggle"
            className="h-9 px-3 border-2 border-slate-300 hover:border-brand">
            <FolderOpen className="w-4 h-4 mr-1.5" />
            <span className="font-semibold">Bozze</span>
            {bozze.length > 0 && (
              <span className="ml-1.5 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-white text-[11px] font-bold">
                {bozze.length}
              </span>
            )}
            {bozzaId && (
              <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide bg-amber-100 text-amber-800 border border-amber-200 rounded-full px-1.5 py-0.5">
                in corso
              </span>
            )}
            <ChevronRight className={`w-4 h-4 ml-1 text-slate-500 transition-transform ${showBozze ? "rotate-90" : ""}`} />
          </Button>
          <select data-testid="storico-filtro-condominio" value={filtroStorico} onChange={(e) => setFiltroStorico(e.target.value)}
            className="h-11 px-3 border-2 border-slate-300 rounded-lg bg-white text-base max-w-full w-full sm:w-auto truncate">
            <option value="">Tutti i condomìni</option>
            {condomini.map((c) => <option key={c.id} value={c.id}>{c.bacheca_ready ? "🟢 " : ""}{c.nome}</option>)}
          </select>
        </div>
        {/* Iter123k249 · pannello bozze espandibile sotto il titolo */}
        {showBozze && (
          <Card className="p-3 sm:p-4 mb-3 border-2 border-brand/30" data-testid="bacheca-bozze-list-card">
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="text-sm font-bold text-slate-700">
                {bozze.length === 0 ? "Nessuna bozza salvata" : `${bozze.length} bozza${bozze.length === 1 ? "" : "e"} salvat${bozze.length === 1 ? "a" : "e"}`}
              </div>
              {bozzaId && (
                <Button type="button" variant="outline" size="sm"
                  data-testid="bacheca-nuova-bozza"
                  onClick={nuovaBozza} className="h-8">
                  <FilePlus className="w-3.5 h-3.5 mr-1" /> Nuova
                </Button>
              )}
            </div>
            <div className="space-y-2" data-testid="bacheca-bozze-list">
              {bozze.length === 0 && (
                <div className="text-xs text-slate-500 p-3 text-center bg-slate-50 rounded-lg">
                  Scrivi un avviso e usa <b>Salva come bozza</b> per riprenderlo dopo.
                </div>
              )}
              {bozze.map((b) => (
                <div key={b.id} role="button" tabIndex={0}
                  onClick={() => caricaBozza(b)}
                  onKeyDown={(e) => { if (e.key === "Enter") caricaBozza(b); }}
                  data-testid={`bacheca-bozza-${b.id}`}
                  className={`p-2.5 rounded-lg border-2 cursor-pointer transition-colors ${
                    bozzaId === b.id
                      ? "border-brand bg-[#FBEEF3]/60"
                      : "border-slate-200 hover:border-brand hover:bg-[#FBEEF3]/30"
                  }`}>
                  <div className="flex items-start justify-between gap-2 min-w-0">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5 min-w-0">
                        <h3 className="font-bold text-sm text-slate-800 truncate">
                          {b.titolo || <span className="italic text-slate-400">(senza titolo)</span>}
                        </h3>
                        <PrioBadge p={b.priorita} />
                      </div>
                      <div className="text-[11px] text-slate-500 mt-0.5 flex items-center gap-2 flex-wrap">
                        <span>Scope: <b>{b.scope}</b></span>
                        {(b.allegati || []).length > 0 && <span>· {b.allegati.length} allegato/i</span>}
                        {b.updated_at && <span>· aggiornata {fmtDate(b.updated_at)}</span>}
                      </div>
                    </div>
                    <button data-testid={`del-bozza-${b.id}`}
                      onClick={(e) => eliminaBozza(b, e)}
                      className="text-slate-400 hover:text-red-600 shrink-0 p-1">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
        <div className="space-y-2">
          {storicoFiltrato.length === 0 && <Card className="p-5 text-center text-slate-500">{filtroStorico ? "Nessun avviso per il condominio selezionato." : "Nessun avviso inviato."}</Card>}
          {storicoFiltrato.map((a) => (
            <Card key={a.id} role="button" tabIndex={0} onClick={() => setDetail(a)}
              onKeyDown={(e) => { if (e.key === "Enter") setDetail(a); }}
              className="p-3 border-2 cursor-pointer transition-colors hover:border-brand hover:bg-[#FBEEF3]/40" data-testid={`storico-${a.id}`}>
              <div className="flex items-start justify-between gap-2 min-w-0">
                <h3 className="font-bold text-base text-slate-800 truncate min-w-0">{a.titolo}</h3>
                <div className="flex items-center gap-1.5 shrink-0">
                  <PrioBadge p={a.priorita} />
                  <button data-testid={`del-avviso-${a.id}`} onClick={(e) => { e.stopPropagation(); elimina(a); }} className="text-slate-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
              <div className="flex items-center gap-1.5 text-xs font-semibold text-brand min-w-0" data-testid={`storico-dest-${a.id}`}>
                <Users className="w-3.5 h-3.5 shrink-0" />
                <span className="truncate min-w-0">A: {a.destinatari_label}{a.condomini_label ? ` · ${a.condomini_label}` : ""}</span>
              </div>
              <p className="text-xs text-slate-600 mt-0.5 line-clamp-1 whitespace-pre-wrap break-words">{a.testo}</p>
              <div className="text-xs text-slate-400 mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                <span>{fmtDate(a.created_at)}</span>
                <span className="flex items-center gap-1" title="Email inviate / destinatari" data-testid={`storico-email-${a.id}`}><Mail className="w-3 h-3" /> {a.email_inviati != null ? a.email_inviati : "—"}/{a.destinatari_count}</span>
                {a.allegati?.length > 0 && <span className="flex items-center gap-1"><Paperclip className="w-3 h-3" /> {a.allegati.length}</span>}
                <span className="ml-auto flex items-center gap-1 font-semibold text-brand">Apri <ChevronRight className="w-3.5 h-3.5" /></span>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-auto" data-testid="avviso-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-2xl font-display pr-6">
                  <Mail className="w-6 h-6 text-brand shrink-0" /> {detail.titolo}
                </DialogTitle>
                <DialogDescription className="sr-only">Dettaglio avviso inviato ai condòmini</DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <PrioBadge p={detail.priorita} />
                <span className="text-xs text-slate-400">{fmtDate(detail.created_at)}</span>
                {detail.created_by && <span className="text-xs text-slate-400">· da {detail.created_by}</span>}
              </div>
              <div className="rounded-xl border-2 border-slate-100 overflow-hidden">
                <div className="bg-slate-50 px-4 py-3 text-sm space-y-1 border-b">
                  <div className="flex gap-2"><span className="font-semibold text-slate-500 w-20 shrink-0">Destinatari</span><span className="text-slate-800 font-semibold" data-testid="detail-destinatari">{detail.destinatari_label} ({detail.destinatari_count})</span></div>
                  <div className="flex gap-2"><span className="font-semibold text-slate-500 w-20 shrink-0">Email</span><span className="text-slate-800 font-semibold" data-testid="detail-email-inviate">{detail.email_inviati != null ? detail.email_inviati : "—"} inviate su {detail.destinatari_count} destinatari</span></div>
                  {detail.condomini_label && (
                    <div className="flex gap-2"><span className="font-semibold text-slate-500 w-20 shrink-0">Condominio</span><span className="text-slate-800 font-semibold" data-testid="detail-condominio">{detail.condomini_label}</span></div>
                  )}
                  {detail.destinatari_count > 1 && detail.destinatari_nomi?.length > 0 && (
                    <details className="group" data-testid="detail-destinatari-dropdown">
                      <summary className="cursor-pointer font-semibold text-brand flex items-center gap-1 list-none select-none">
                        <ChevronRight className="w-4 h-4 transition-transform group-open:rotate-90" /> Visualizza elenco destinatari ({detail.destinatari_nomi.length})
                      </summary>
                      <div className="mt-2 flex flex-wrap gap-1.5 max-h-40 overflow-y-auto" data-testid="detail-destinatari-nomi">
                        {detail.destinatari_nomi.map((n, i) => (
                          <span key={i} className="inline-block rounded-full bg-white border border-slate-200 px-2 py-0.5 text-slate-700">{n}</span>
                        ))}
                      </div>
                    </details>
                  )}
                  <div className="flex gap-2"><span className="font-semibold text-slate-500 w-20 shrink-0">Oggetto</span><span className="text-slate-800">{emailSubject(detail)}</span></div>
                </div>
                <div className="px-4 py-4 text-base text-slate-700 whitespace-pre-wrap" data-testid="detail-corpo">{emailBody(detail, titolare)}</div>
              </div>
              {detail.allegati?.length > 0 && (
                <div className="mt-4">
                  <div className="text-sm font-semibold text-slate-600 mb-1">Allegati</div>
                  <Allegati list={detail.allegati} />
                </div>
              )}
              <div className="mt-5 flex flex-wrap gap-2 justify-end">
                {/* Iter122fi · Stampa / Esporta PDF: apre una window con SOLO il corpo dell'avviso
                    (data · titolo · firma · testo · allegati names) senza il riquadro metadati
                    (destinatari/email/condominio/oggetto). PDF sfrutta la stampa nativa del browser
                    salvando come PDF. */}
                <Button data-testid="avviso-stampa-btn" variant="outline"
                  onClick={() => stampaAvviso(detail, titolare, false)} className="font-bold">
                  <Printer className="w-4 h-4 mr-2" /> Stampa
                </Button>
                <Button data-testid="avviso-pdf-btn" variant="outline"
                  onClick={() => stampaAvviso(detail, titolare, true)} className="font-bold">
                  <FileDown className="w-4 h-4 mr-2" /> Esporta PDF
                </Button>
                <Button data-testid="avviso-reinvia-btn" disabled={resending} onClick={() => reinvia(detail)}
                  className="bg-brand hover:bg-brand-dark font-bold">
                  <Send className="w-4 h-4 mr-2" /> {resending ? "Reinvio..." : "Reinvia agli stessi destinatari"}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
